import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

import UploadCloudIcon from '../../../../GlobalComponents/IconComponents/UploadCloudIcon';
import UploadIcon from '../../../../GlobalComponents/IconComponents/UploadIcon';
import FileIcon from '../../../../GlobalComponents/IconComponents/FileIcon';
import TrashIcon from '../../../../GlobalComponents/IconComponents/TrashIcon';
import ImageIcon from '../../../../GlobalComponents/IconComponents/ImageIcon';
import Snackbars from '../../../../GlobalComponents/Alerts/Snackbars';

import { AddAttachment } from '../../../../ReduxTLK/features/Attachement/AttachementSlice';
import {
  resetEditTicketDraftByIndex,
  setEditTicketDraft,
} from '../../../../ReduxTLK/features/SAV/NewTicketDraftSlice';
import { Persistor } from '../../../..';

import styles from './UploadFile.module.css';

const ALLOWED_FILE_TYPES = ['png', 'jpeg', 'jpg', 'pdf', 'docx', 'doc', 'xlsx', 'txt'];

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const generateRandomId = () =>
  Math.random().toString(36).substr(2, 9);

const generateUniqueRandomId = (existing) => {
  let newId;
  do {
    newId = generateRandomId();
  } while (existing.some((item) => item.id === newId));
  return newId;
};

export default function UploadFile(props) {
  const dispatch = useDispatch();
  const editedTicketDraft = useSelector(
    (state) => state.draftNewTicket.editedTicketDraft
  );

  const [open, setOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [form, setForm] = useState(new FormData());
  const [isDragging, setIsDragging] = useState(false);

  const [message, setMessage] = useState();
  const [openErrorBox, setOpenErrorBox] = useState(false);
  const [openSuccessBox, setOpenSuccessBox] = useState(false);

  const inputFile = useRef(null);

  const openModal = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setSelectedFiles([]);
    setForm(new FormData());
    setIsDragging(false);
  };

  const handleAttach = () => {
    inputFile.current?.click();
  };

  const addFile = (file) => {
    if (!file) return;

    const fileObj = {
      id: generateUniqueRandomId(selectedFiles),
      file,
    };

    let editedTicketDraftCopy = [...editedTicketDraft];
    const existingIndex = editedTicketDraftCopy.findIndex(
      (ticket) => ticket.id === props.ticket
    );
    if (existingIndex === -1) {
      editedTicketDraftCopy = [
        ...editedTicketDraftCopy,
        { updatedItem: {}, id: props.ticket },
      ];
    }
    dispatch(setEditTicketDraft(editedTicketDraftCopy));
    Persistor.persist();

    setSelectedFiles((prev) => [...prev, fileObj]);
  };

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    addFile(file);
    event.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    files.forEach((f) => addFile(f));
  };

  useEffect(() => {
    const newForm = new FormData();
    selectedFiles.forEach((item) => {
      newForm.append('fichiers', item.file);
    });
    setForm(newForm);
  }, [selectedFiles]);

  const handleSave = () => {
    if (selectedFiles.length === 0) return;

    const invalidFiles = selectedFiles.filter(
      (item) =>
        !ALLOWED_FILE_TYPES.includes(
          item.file.name?.toLowerCase().split('.').pop()
        )
    );
    if (invalidFiles.length > 0) {
      setMessage(
        'Type de fichier non valide. Types autorisés : PDF, DOCX, TXT, JPEG, JPG, PNG.'
      );
      setOpenErrorBox(true);
      return;
    }

    const invalidSizes = selectedFiles.filter((item) => {
      const isImage = item.file.type?.startsWith('image');
      const maxBytes = (isImage ? 5 : 2) * 1024 * 1024;
      return item.file.size > maxBytes;
    });
    if (invalidSizes.length > 0) {
      setMessage(
        'La taille du fichier dépasse la limite autorisée (Images : 5 Mo, autres : 2 Mo).'
      );
      setOpenErrorBox(true);
      return;
    }

    const data = { idTicket: props.ticket, array: form };

    try {
      dispatch(AddAttachment(data)).then(() => {
        dispatch(resetEditTicketDraftByIndex(props.ticket));
        props.fetchData(props.paginationModelCustom);
      });

      setMessage('Vos fichiers ont été ajoutés avec succès.');
      setOpenSuccessBox(true);
      handleClose();
    } catch (err) {
      setMessage('Un problème est survenu lors de la sauvegarde des fichiers.');
      setOpenErrorBox(true);
      handleClose();
    }
  };

  const resetSelection = (id) => {
    setSelectedFiles((prev) => prev.filter((item) => item.id !== id));
  };

  const getFileIcon = (type) => {
    if (!type) return <FileIcon />;
    if (type.includes('image')) return <ImageIcon />;
    return <FileIcon />;
  };

  return (
    <>
      <div className={styles.triggerWrap}>
        <Button
          className={styles.triggerButton}
          onClick={openModal}
          startIcon={<UploadIcon color="#ffffff" />}
        >
          Importer un fichier
        </Button>
      </div>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        className={styles.dialog}
      >
        <DialogTitle className={styles.dialogHeader}>
          <div className={styles.dialogHeaderRow}>
            <div className={styles.dialogHeaderText}>
              <span className={styles.dialogTitle}>Ajouter fichier(s)</span>
              <span className={styles.dialogSubtitle}>
                Importer et attacher un ou plusieurs fichier(s)
              </span>
            </div>
            <IconButton
              className={styles.dialogCloseBtn}
              onClick={handleClose}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          <div
            className={`${styles.dropzone} ${
              isDragging ? styles.dropzoneActive : ''
            }`}
            onClick={handleAttach}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className={styles.dropzoneIcon}>
              <UploadCloudIcon />
            </div>
            <div className={styles.dropzoneTitle}>
              <span className={styles.dropzoneTitleAction}>
                Parcourir vos fichiers
              </span>
              <span>ou glissez-déposez ici</span>
            </div>
            <p className={styles.dropzoneHint}>
              PDF, DOCX, TXT (max 2 Mo) · JPEG, PNG (max 5 Mo)
            </p>
            <input
              type="file"
              id="file"
              ref={inputFile}
              className={styles.hiddenInput}
              onChange={handleFileChange}
            />
          </div>

          {selectedFiles.length > 0 && (
            <div className={styles.fileList}>
              {selectedFiles.map((item) => (
                <div key={item.id} className={styles.fileItem}>
                  <div className={styles.fileItemIcon}>
                    {getFileIcon(item.file.type)}
                  </div>
                  <div className={styles.fileItemBody}>
                    <span
                      className={styles.fileItemName}
                      title={item.file.name}
                    >
                      {item.file.name}
                    </span>
                    <span className={styles.fileItemSize}>
                      {formatBytes(item.file.size)}
                    </span>
                  </div>
                  <div className={styles.fileItemActions}>
                    <div className={styles.fileItemCheck}>
                      <CheckIcon sx={{ fontSize: 14 }} />
                    </div>
                    <button
                      type="button"
                      className={styles.fileItemDelete}
                      onClick={() => resetSelection(item.id)}
                      aria-label="Supprimer le fichier"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button
            className={styles.cancelButton}
            onClick={handleClose}
            variant="outlined"
          >
            Annuler
          </Button>
          <Button
            className={styles.saveButton}
            onClick={handleSave}
            variant="contained"
            disabled={selectedFiles.length === 0}
          >
            Sauvegarder
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbars
        severity="success"
        message={message}
        open={openSuccessBox}
        setOpen={setOpenSuccessBox}
        autoHideDuration={1000}
      />
      <Snackbars
        severity="error"
        message={message}
        open={openErrorBox}
        setOpen={setOpenErrorBox}
        autoHideDuration={1000}
      />
    </>
  );
}